import React, { useEffect, useState } from 'react'
import { Popover, Tooltip, theme } from 'antd'
import { DownOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Dayjs } from 'dayjs'
import { Button } from '../Button'
import { DateNavigatorUnit, DateNavigatorWindow, DisabledMonth, WeekStart, containsToday, startOfQuarter, stepWindow, todayWindow, windowDisabled, windowFor } from './window'

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
}

/** Every word the control renders. English defaults; the DS takes no i18n dependency, so a real
 * call site passes translations the same way `FilterTrigger` and `DataTable` take theirs. */
export interface DateNavigatorLabels {
  previous: string
  next: string
  /** Accessible name of the title button, prefixed to the period it currently shows. */
  changePeriod: string
  /** Heading over the panel's period column - visible, and the column's accessible name. */
  selectBy: string
  week: string
  month: string
  quarter: string
  year: string
  /** Why a month is offered but cannot be picked - the tooltip on an empty month, and the part of
   * that month's accessible name that says why it is inert. */
  noData: string
  /** The "back to now" button, named for the unit it will land on. */
  thisWeek: string
  thisMonth: string
  thisQuarter: string
  thisYear: string
  /** The panel's own year controls: they browse the grid, they do not move the window. */
  changeYear: string
  previousYear: string
  nextYear: string
}

const DEFAULT_LABELS: DateNavigatorLabels = {
  previous: 'Previous period',
  next: 'Next period',
  changePeriod: 'Change period',
  selectBy: 'Select by',
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year',
  noData: 'No data for this month',
  thisWeek: 'This week',
  thisMonth: 'This month',
  thisQuarter: 'This quarter',
  thisYear: 'This year',
  changeYear: 'Change year',
  previousYear: 'Previous year',
  nextYear: 'Next year',
}

export interface DateNavigatorProps {
  value: DateNavigatorWindow
  onChange: (value: DateNavigatorWindow) => void
  /** Units this surface offers as peers, in display order. @default ['month'] */
  units?: DateNavigatorUnit[]
  /** Which months hold nothing to show, by first day. Such a month is drawn in the grid but cannot
   * be picked, carrying `labels.noData` as a tooltip and in its accessible name; the steppers and
   * the back-to-now button go flat rather than landing you somewhere empty. */
  disabledMonth?: DisabledMonth
  /** false drops the "back to now" button, for a surface with no meaningful "now". @default true */
  showToday?: boolean
  /** false makes the title plain text: arrows only, no panel. For a surface with so few periods
   * that stepping reaches them all, where a jump would be a menu of two or three. @default true */
  showPicker?: boolean
  /** Where this surface's week starts (0 Sunday … 6 Saturday). Omit to follow the dayjs locale.
   * Pass it whenever the surface has its own week-start setting, or a week window will be built on
   * a different boundary from the one the surface draws. */
  weekStart?: WeekStart
  labels?: Partial<DateNavigatorLabels>
  /** Overrides the title text. The default reads dayjs's active locale; a call site with
   * react-intl passes its own formatter so month names follow the user's language. */
  formatLabel?: (value: DateNavigatorWindow) => string
  size?: 'small' | 'medium'
}

/** Compact inclusive range: collapses whatever the two ends share. */
export function formatRange(start: Dayjs, end: Dayjs): string {
  const sameYear = start.year() === end.year()
  if (sameYear && start.month() === end.month()) return `${start.format('D')} – ${end.format('D MMM YYYY')}`
  if (sameYear) return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`
  return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`
}

export function defaultFormatLabel(value: DateNavigatorWindow): string {
  if (value.span === 'year') return value.start.format('YYYY')
  if (value.span === 'month') return value.start.format('MMMM YYYY')
  if (value.span === 'quarter') return `Q${Math.floor(value.start.month() / 3) + 1} ${value.start.format('YYYY')}`
  return formatRange(value.start, value.end)
}

/** How far either way the year dropdown reaches. The two arrows beside it page a year at a time and
 * carry the list with them, so this bounds the menu, not where the navigator can go. */
const YEAR_MENU_REACH = 5

/**
 * Vacation Tracker's date navigator: the four jobs of moving through time in one control -
 * which unit you are looking at, stepping by it, jumping to a named one, and getting back to now.
 *
 * The app has five rival implementations of this today and no two agree (the wallchart's arrows
 * step a fixed week however long its range is, and a week is shorter than the 14 days its own
 * picker allows; the calendar and the dashboard heatmap can step but not jump; both report
 * controls jump but cannot step). The one rule that fixes all of it: **the stepper always moves by
 * the unit that is selected**, custom included, where it moves by the custom window's own length.
 *
 * The row is one segmented group - step back, the period, step forward, and beside them the way
 * back to now, named for the unit it will land on ("This month", not "Today"). Everything about
 * *which* period you are moving by lives in the panel the period opens, under "Select by", so the
 * row costs the same width whatever the surface offers.
 */
export function DateNavigator({
  value,
  onChange,
  units = ['month'],
  disabledMonth,
  showToday = true,
  showPicker = true,
  weekStart,
  labels,
  formatLabel = defaultFormatLabel,
  size = 'medium',
}: DateNavigatorProps) {
  const { token } = theme.useToken()
  const text = { ...DEFAULT_LABELS, ...labels }
  // useId returns `:r1:`, which is not a legal CSS selector.
  const columnHeadingId = `vt-date-navigator-${React.useId().replace(/:/g, '')}`

  const [open, setOpen] = useState(false)
  const [yearOpen, setYearOpen] = useState(false)
  const [browse, setBrowse] = useState<Dayjs>(value.start)

  // The panel is a jump affordance, not a second source of truth: every open starts from where the
  // navigator actually is, so a browse that was abandoned last time cannot resurface.
  useEffect(() => {
    if (!open) return
    setBrowse(value.start)
    setYearOpen(false)
  }, [open, value.start])

  const gridUnit: DateNavigatorUnit = value.span
  const label = formatLabel(value)
  const buttonSize = size === 'small' ? 'small' : 'medium'
  const titleWidth = size === 'small' ? 108 : 132

  const commit = (next: DateNavigatorWindow) => {
    onChange(next)
    setOpen(false)
  }

  // Choosing a unit commits it and leaves the panel open, so the grid beside it becomes that unit's
  // and the jump can follow in the same visit. Choosing a cell, a named span or Apply closes.
  const chooseUnit = (unit: DateNavigatorUnit) => {
    onChange(windowFor(unit, value.start, weekStart))
  }

  const periodCount = units.length
  const nowLabel = { week: text.thisWeek, month: text.thisMonth, quarter: text.thisQuarter, year: text.thisYear }[value.span]

  const stepTo = (direction: -1 | 1) => stepWindow(value, direction, weekStart)
  const nowTarget = todayWindow(value, undefined, weekStart)

  interface GridCell {
    key: React.Key
    active: boolean
    empty: boolean
    /** What the cell reads as to a screen reader. The visible content can be two lines, or a month
     * name that says nothing about why it is inert, so the name is built rather than inferred. */
    name: string
    content: React.ReactNode
    onClick: () => void
  }

  // An empty cell is aria-disabled rather than disabled: `disabled` takes a button out of the tab
  // order, which would leave the reason reachable by mouse only, and antd suppresses a tooltip on a
  // disabled child for the same reason. It stays focusable, says why in its own name, and does
  // nothing when pressed.
  const gridCell = ({ key, active, empty, name, content, onClick }: GridCell) => {
    const cell = (
      <Button
        key={key}
        variant={active ? 'primary' : 'outline'}
        aria-disabled={empty || undefined}
        aria-label={name}
        onClick={empty ? () => undefined : onClick}
        style={{
          height: 'auto',
          paddingTop: token.paddingSM,
          paddingBottom: token.paddingSM,
          ...(empty ? { color: token.colorTextDisabled, cursor: 'not-allowed' } : {}),
        }}
      >
        {content}
      </Button>
    )
    return empty ? (
      <Tooltip key={key} title={text.noData}>
        {cell}
      </Tooltip>
    ) : (
      cell
    )
  }

  const monthGrid = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: token.paddingXS }}>
      {Array.from({ length: 12 }, (_, month) => {
        const candidate = browse.month(month).date(1)
        const empty = windowDisabled(windowFor('month', candidate), disabledMonth)
        const monthName = candidate.format('MMMM')
        return gridCell({
          key: month,
          active: value.start.isSame(candidate, 'month'),
          empty,
          name: empty ? `${monthName}, ${text.noData}` : monthName,
          content: monthName,
          onClick: () => commit(windowFor(gridUnit, candidate, weekStart)),
        })
      })}
    </div>
  )

  // Two lines, because a quarter is the one unit whose name does not say when it is: "Q3" alone
  // sends you counting on your fingers, and the months underneath answer it.
  const quarterGrid = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: token.paddingXS }}>
      {Array.from({ length: 4 }, (_, quarter) => {
        const candidate = browse.month(quarter * 3).date(1)
        const window = windowFor('quarter', candidate)
        const empty = windowDisabled(window, disabledMonth)
        const months = `${candidate.format('MMM')} – ${candidate.add(2, 'month').format('MMM')}`
        const quarterName = `Q${quarter + 1} ${months}`
        return gridCell({
          key: quarter,
          active: value.span === 'quarter' && value.start.isSame(startOfQuarter(candidate), 'day'),
          empty,
          name: empty ? `${quarterName}, ${text.noData}` : quarterName,
          content: (
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <span>{`Q${quarter + 1}`}</span>
              <span style={{ fontSize: token.fontSizeSM, opacity: 0.7 }}>{months}</span>
            </span>
          ),
          onClick: () => commit(window),
        })
      })}
    </div>
  )

  // The year unit's grid IS years, so it needs no year browser above it - that header would be a
  // second, rival way to say the thing the cells already say.
  const yearGrid = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: token.paddingXS }}>
      {Array.from({ length: YEAR_MENU_REACH * 2 + 1 }, (_, index) => {
        const candidate = browse.year(browse.year() - YEAR_MENU_REACH + index).startOf('year')
        const window = windowFor('year', candidate)
        const empty = windowDisabled(window, disabledMonth)
        const yearName = candidate.format('YYYY')
        return gridCell({
          key: yearName,
          active: value.span === 'year' && value.start.isSame(candidate, 'year'),
          empty,
          name: empty ? `${yearName}, ${text.noData}` : yearName,
          content: yearName,
          onClick: () => commit(window),
        })
      })}
    </div>
  )

  const yearMenu = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: token.paddingXXS }}>
      {Array.from({ length: YEAR_MENU_REACH * 2 + 1 }, (_, index) => {
        const year = browse.year() - YEAR_MENU_REACH + index
        return (
          <Button
            key={year}
            size="small"
            variant={year === browse.year() ? 'primary' : 'text'}
            onClick={() => {
              setBrowse(browse.year(year))
              setYearOpen(false)
            }}
          >
            {year}
          </Button>
        )
      })}
    </div>
  )

  const periodColumn = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 132,
        paddingRight: token.paddingSM,
        borderRight: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <span
        id={columnHeadingId}
        style={{
          color: token.colorTextSecondary,
          fontSize: token.fontSizeSM,
          padding: `${token.paddingXXS}px ${token.paddingXS}px ${token.paddingXS}px`,
        }}
      >
        {text.selectBy}
      </span>

      {/* Listbox rather than a radiogroup to match `FilterTrigger`'s single-select panel, which is
          the design system's existing shape for "pick one of these" inside a popover. */}
      <div role="listbox" aria-labelledby={columnHeadingId} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {units.map((unit) => {
          const selected = value.span === unit
          return (
            <Button
              key={unit}
              role="option"
              aria-selected={selected}
              variant="text"
              onClick={() => chooseUnit(unit)}
              style={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                // Neutral, not primary: the fill says "this is where you are in the list", and the
                // one primary in the panel is the cell you are about to land on.
                background: selected ? token.colorFillSecondary : undefined,
              }}
            >
              {text[unit]}
            </Button>
          )
        })}
      </div>
    </div>
  )

  const jumpPane = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: token.padding, minWidth: gridUnit === 'year' ? 264 : 344 }}>
      {gridUnit !== 'year' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: token.paddingXS }}>
          <Popover
            open={yearOpen}
            onOpenChange={setYearOpen}
            trigger="click"
            placement="bottomLeft"
            arrow={false}
            content={yearMenu}
            // Inside the navigator's own popover rather than in a portal of its own: a click in a
            // sibling portal reads as a click outside this panel, which closed it under the menu.
            getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
          >
            <Button
              variant="text"
              iconPosition="end"
              icon={<DownOutlined style={{ fontSize: 10 }} />}
              aria-haspopup="listbox"
              aria-expanded={yearOpen}
              aria-label={`${text.changeYear}: ${browse.format('YYYY')}`}
              style={{ fontWeight: 600, fontSize: token.fontSizeLG, paddingInline: token.paddingXS }}
            >
              {browse.format('YYYY')}
            </Button>
          </Popover>

          <div style={{ display: 'flex', gap: token.paddingXXS }}>
            <Button variant="outline" iconOnly icon={<LeftOutlined />} aria-label={text.previousYear} onClick={() => setBrowse(browse.subtract(1, 'year'))} />
            <Button variant="outline" iconOnly icon={<RightOutlined />} aria-label={text.nextYear} onClick={() => setBrowse(browse.add(1, 'year'))} />
          </div>
        </div>
      )}
      {gridUnit === 'year' ? yearGrid : gridUnit === 'quarter' ? quarterGrid : monthGrid}
    </div>
  )

  // One panel holds both halves of the job: which period on the left, which one on the right. The
  // column is dropped when there is nothing to choose between - a month-only surface with no custom
  // range would otherwise show a list of one.
  const panel = (
    <div style={{ display: 'flex', gap: token.paddingSM, alignItems: 'stretch' }}>
      {periodCount > 1 && periodColumn}
      {jumpPane}
    </div>
  )

  // The three stepping controls read as one thing because they are one thing - the period and the
  // two ways to move it by exactly that period. "Now" is a different job, so it sits past a rule
  // rather than outside the border.
  const segment: React.CSSProperties = { borderRadius: 0, borderWidth: 0 }
  const rule = <span style={{ width: 1, background: token.colorBorder, alignSelf: 'stretch' }} />

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'stretch',
          border: `1px solid ${token.colorBorder}`,
          borderRadius: token.borderRadius,
          overflow: 'hidden',
          background: token.colorBgContainer,
        }}
      >
        <Button
          size={buttonSize}
          variant="text"
          icon={<LeftOutlined />}
          aria-label={text.previous}
          disabled={windowDisabled(stepTo(-1), disabledMonth)}
          onClick={() => onChange(stepTo(-1))}
          style={segment}
        />

        {showPicker ? (
          <Popover open={open} onOpenChange={setOpen} trigger="click" placement="bottomLeft" arrow={false} content={panel}>
            <Button
              size={buttonSize}
              variant="text"
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-label={`${text.changePeriod}: ${label}`}
              style={{
                ...segment,
                // Fixed, because the two arrows sit against it: a title that resizes with the month
                // moves the arrow out from under the cursor between one step and the next.
                minWidth: titleWidth,
                background: open ? token.colorFillSecondary : undefined,
              }}
            >
              {label}
            </Button>
          </Popover>
        ) : (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: titleWidth,
              paddingInline: token.paddingSM,
              fontSize: size === 'small' ? token.fontSizeSM : token.fontSize,
              color: token.colorText,
            }}
          >
            {label}
          </span>
        )}

        <Button
          size={buttonSize}
          variant="text"
          icon={<RightOutlined />}
          aria-label={text.next}
          disabled={windowDisabled(stepTo(1), disabledMonth)}
          onClick={() => onChange(stepTo(1))}
          style={segment}
        />

        {showToday && (
          <>
            {rule}
            <Button
              size={buttonSize}
              variant="text"
              disabled={containsToday(value) || windowDisabled(nowTarget, disabledMonth)}
              onClick={() => onChange(nowTarget)}
              style={segment}
            >
              {nowLabel}
            </Button>
          </>
        )}
      </div>

      {/* The window is the thing that changed, and every control that changes it is a different
          button - so the announcement belongs to the group, not to whichever button was pressed. */}
      <span aria-live="polite" style={VISUALLY_HIDDEN}>
        {label}
      </span>
    </div>
  )
}

export default DateNavigator
